import { CardStatusEnum, MyProjectsProject, RepProjectPanel } from '@platform/client/schemas';
import {
  Accordion,
  AlertIconTooltip,
  NumericBadge,
  PillBadge,
  PillBadgeColors,
} from '@platform/components';
import { ProjectType } from '..';
import FlexibleLink from '../../../util/FlexibleLink';
import { RepUserCard } from '../RepUserCard';
import { RepUserTeamCard } from '../TeamCards';
import EmptyProjectIntroductionPanel from './EmptyProjectIntroductionPanel';
import { ProjectActionDropdown } from './ProjectActionDropdown';

/** Shows a list of information describing the relationships with professionals as they relate
 * to one project. This information can be sources from an Introduction, a Discovery, or an
 * ongoing/complete project assignment. */
export const RepUserProjectPanel = (props: RepProjectPanel) => {
  const { project, isCustom, inProgress, actionRequired, inactive, completed, teamCards } = props;
  // combinedPro cards is used primarily to access a pro data to use on custom projects
  const combinedProCards = [...actionRequired, ...inProgress, ...inactive];
  const isPanelEmpty = !combinedProCards.length && !teamCards.length;
  const activeTeamCards = teamCards.filter(
    (card) => card.cardStatus !== CardStatusEnum.project_complete,
  );
  const completedTeamCards = teamCards.filter(
    (card) => card.cardStatus === CardStatusEnum.project_complete,
  );

  const [statusBadgeText, statusBadgeColor] = getProjectStatusBadge(props);
  const badges: { text: string; color: PillBadgeColors }[] = [];
  if (statusBadgeText && statusBadgeColor) {
    badges.push({
      text: statusBadgeText,
      color: statusBadgeColor,
    });
  }

  if (project?.roleType) {
    badges.unshift({
      text: ProjectType[project.roleType],
      color: 'secondary-brand-25',
    });
  } else {
    badges.unshift({
      text: 'Custom',
      color: 'secondary-brand-25',
    });
  }

  return (
    <div
      className='p-6 rounded-md border border-gray-100 shadow-md bg-white flex flex-col gap-4'
      data-testid={
        !isCustom ?
          `project-${project?.id}`
        : `custom-project-${combinedProCards[0].professional.id}`
      }
    >
      <div>
        {!isCustom && (
          <div className='flex flex-row justify-between items-center'>
            <div className='flex justify-center md:justify-normal flex-row gap-2'>
              {badges.map((badge) => {
                return (
                  <PillBadge key={badge.text} variant='plain' size='md' color={badge.color}>
                    {badge.text}
                  </PillBadge>
                );
              })}
            </div>
            <ProjectActionDropdown panelData={props} />
          </div>
        )}
        <div className='border-b border-gray-100 flex flex-col md:flex-row justify-between pb-6 font-semibold text-lg'>
          {!isCustom && (
            <FlexibleLink
              className='text-brand-600'
              type='external'
              newTab={true}
              href={`/platform/directory/projects/${project?.id}`}
            >
              {project?.name}
            </FlexibleLink>
          )}
          {isCustom && (
            <div className='flex justify-left gap-2 items-center'>
              {`Custom Project with ${combinedProCards[0].professional.firstname} ${combinedProCards[0].professional.lastname}`}
              <div className='hidden md:inline mt-2'>
                <AlertIconTooltip
                  size='md'
                  placement='bottom end'
                  header="What's a custom project?"
                  supportingText='This Experteer is interested in collaborating with you to create a project that is a good fit for both of you. If you connect, you can discuss more of these details in your discovery call. Once you match, the Experteer will update the project name.'
                />
              </div>
            </div>
          )}
          {!isCustom && (
            <div className='text-xs text-gray-400 mt-2 font-normal'>
              {getProjectStatusText(props)}
            </div>
          )}
        </div>
      </div>
      <>
        {!isPanelEmpty && !completed && !isCustom && (
          <>
            <Accordion
              type='custom'
              className='py-2 mb-2 mt-6'
              isBorderless={true}
              titleContent={
                <div>
                  Action Items{' '}
                  {actionRequired.length + activeTeamCards.length > 0 && (
                    <NumericBadge
                      size='sm'
                      color={'error'}
                      number={actionRequired.length + activeTeamCards.length}
                    />
                  )}
                </div>
              }
            >
              <div className='flex flex-col gap-6 justify-center items-center mt-1'>
                {actionRequired.map((card, i) => (
                  <RepUserCard key={`rep-action-req-${i}`} cardData={card} project={project} />
                ))}
                {activeTeamCards.map((card, i) => (
                  <RepUserTeamCard
                    key={`${card.project.id}-team-${i}`}
                    cardData={card}
                    project={project as MyProjectsProject}
                  />
                ))}
              </div>
            </Accordion>
            <Accordion
              type='custom'
              className='py-2 mb-2 mt-6'
              isBorderless={true}
              initiallyClosed={true}
              titleContent={
                <div>
                  Pending{' '}
                  {inProgress.length > 0 && (
                    <NumericBadge number={inProgress.length} size='sm' color='gray' />
                  )}
                </div>
              }
            >
              <div className='flex flex-col gap-6 justify-center items-center'>
                {inProgress.map((card, i) => (
                  <RepUserCard key={`rep-in-progress-${i}`} cardData={card} project={project} />
                ))}
              </div>
            </Accordion>
            <Accordion
              type='custom'
              className='py-2 mb-2 mt-6'
              isBorderless={true}
              initiallyClosed={true}
              titleContent={
                <div>
                  Declined/expired{' '}
                  {inactive.length > 0 && (
                    <NumericBadge number={inactive.length} size='sm' color='gray' />
                  )}
                </div>
              }
            >
              <div className='flex flex-col gap-6 justify-center items-center'>
                {inactive.map((card, i) => (
                  <RepUserCard key={`rep-inactive-${i}`} cardData={card} project={project} />
                ))}
              </div>
            </Accordion>
          </>
        )}
        {isPanelEmpty && <EmptyProjectIntroductionPanel projectData={props} />}
        {isCustom && (
          <div className='flex flex-col gap-6 justify-center items-center'>
            {combinedProCards.map((card, i) => (
              <RepUserCard key={`${card.professional.id}-${i}`} cardData={card} project={project} />
            ))}
          </div>
        )}
        {completed && (
          <div className='flex flex-col gap-6 justify-center items-center'>
            {inactive
              .filter((card) => card.cardStatus === CardStatusEnum.project_complete)
              .map((card, i) => (
                <RepUserCard
                  key={`${card.professional.id}-${i}`}
                  cardData={card}
                  project={project}
                />
              ))}
            {completedTeamCards.map((card, i) => (
              <RepUserTeamCard
                key={`${card.project.id}-team-${i}`}
                cardData={card}
                project={project as MyProjectsProject}
              />
            ))}
          </div>
        )}
      </>
    </div>
  );
};

const getProjectStatusText = (projectData: RepProjectPanel) => {
  const { project } = { ...projectData };
  const { publishingStage, publishingStageUpdatedDate, endDate, createdAt } = { ...project };
  if (projectData.completed && endDate) {
    return `Completed ${new Date(endDate)?.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else if (publishingStage === 'published' && publishingStageUpdatedDate) {
    return `Published ${new Date(publishingStageUpdatedDate)?.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else if (publishingStage === 'unpublished' && publishingStageUpdatedDate) {
    return `Unpublished ${new Date(publishingStageUpdatedDate)?.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else if (
    (publishingStage === 'review' || publishingStage === 'revision_requested') &&
    publishingStageUpdatedDate
  ) {
    return `Submitted ${new Date(publishingStageUpdatedDate)?.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else if (publishingStage === 'draft' && createdAt) {
    return `Created ${new Date(createdAt)?.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else {
    throw new Error(
      `Unhandled project status and date. Stage: ${publishingStage}, Stage updated: ${publishingStageUpdatedDate}, Created ${createdAt}`,
    );
  }
};

const getProjectStatusBadge = (
  panelData: RepProjectPanel,
): [string | undefined, PillBadgeColors | undefined] => {
  const { project } = { ...panelData };
  const publishingStage = project?.publishingStage;
  if (publishingStage === 'unpublished' || publishingStage === 'draft') {
    return ['Unpublished', 'gray'];
  } else if (publishingStage === 'review' || publishingStage === 'revision_requested') {
    return ['Under Review', 'gray'];
  } else if (panelData.completed) {
    return ['Completed', 'success-100'];
  } else {
    return ['Published', 'success-500'];
  }
};
